import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, EmptyState, Select, Spinner, TextField, Typography } from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { adminApiClient, type OrderingRecordEventRow, type OrderingReview } from '../lib/adminApiClient';
import { NEUTRAL_BTN, DANGER_BTN } from '../lib/buttonStyles';
import { Pill } from '../components/Pill';

/**
 * Cancellation reviews (Rod, 2026-09-27; Figma Desktop Playground DE21 1955:64).
 *
 * A household paused a second time inside six months stays paused until a
 * person decides here. First pauses lift on their own after 14 days and are
 * listed for visibility only.
 */
type Filter = 'open' | 'auto' | 'decided';

const KIND_LABEL: Record<OrderingRecordEventRow['kind'], string> = {
  delivery_day_cancel: 'Cancelled on delivery day',
  delivery_day_move: 'Moved on delivery day',
  cancel_after_pickup: 'Cancelled after pickup',
  not_home: 'Not home',
  early_move: 'Moved before delivery day',
};

const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
const at = (iso: string) => new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

function eventDetail(e: OrderingRecordEventRow): string {
  const d = e.detail ?? {};
  if (d.feeCoveredBy === 'card') return `${KIND_LABEL[e.kind]} · $${((d.feeCents ?? 700) / 100).toFixed(2)} to the Runner`;
  if (d.feeCoveredBy === 'included_delivery') return `${KIND_LABEL[e.kind]} · included delivery`;
  if (d.from && d.to) return `${KIND_LABEL[e.kind]} · ${d.from} → ${d.to}`;
  return KIND_LABEL[e.kind];
}

const counterLabel = (c: OrderingReview['counter']) => (c === 'late_change' ? 'late changes' : 'early moves');

export default function OrderingReviews() {
  const caps = useCapabilities();
  const canView = caps.has('user:read');
  const canDecide = caps.has('user:manage');
  const [filter, setFilter] = useState<Filter>('open');
  const [reviews, setReviews] = useState<OrderingReview[]>([]);
  const [counts, setCounts] = useState({ open: 0, auto: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.listOrderingReviews(filter);
      setReviews(res.reviews);
      setCounts(res.counts);
      setSelected((cur) => (cur && res.reviews.some((r) => r.pauseId === cur) ? cur : res.reviews[0]?.pauseId ?? null));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  const decide = async (pauseId: string, decision: 'reinstated' | 'kept_paused') => {
    if (!note.trim()) {
      setError('Add a note first — it is the record of why.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await adminApiClient.decideOrderingReview(pauseId, { decision, note: note.trim() });
      setInfo(decision === 'reinstated' ? 'Online ordering reinstated. The customer was told in the app.' : 'Kept paused. Your note is on the record.');
      setNote('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not save');
    } finally {
      setBusy(false);
    }
  };

  const removeEvent = async (e: OrderingRecordEventRow) => {
    if (!note.trim()) {
      setError('Add a note saying what we got wrong before removing an event.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await adminApiClient.voidOrderingRecordEvent(e.id, note.trim());
      setInfo('Event removed. It no longer counts.');
      setNote('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not save');
    } finally {
      setBusy(false);
    }
  };

  if (caps.loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><Spinner color="brand" /></Box>;
  if (!canView) return <Alert severity="error">You don’t have permission to view cancellation reviews.</Alert>;

  const current = reviews.find((r) => r.pauseId === selected) ?? null;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Cancellation reviews</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Customers paused a second time within 6 months (5 late changes in 60 days, or 8 early moves in 30). Ordering
        stays paused until you decide. First pauses lift on their own after 14 days and are listed for visibility.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Select
          ariaLabel="Filter reviews" label="Show" native size="small" sx={{ width: 280 }} value={filter}
          onChange={(_e, v) => setFilter(v as Filter)}
          options={[
            { value: 'open', label: `Waiting for a decision (${counts.open})` },
            { value: 'auto', label: `First pauses, lifting on their own (${counts.auto})` },
            { value: 'decided', label: 'Decided' },
          ]}
        />
      </Box>

      {error && <Box sx={{ mb: 2 }}><Alert severity="error">{error}</Alert></Box>}
      {info && <Box sx={{ mb: 2 }}><Alert severity="success">{info}</Alert></Box>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><Spinner color="brand" /></Box>
      ) : reviews.length === 0 ? (
        <EmptyState
          icon="onlineordering"
          title={filter === 'open' ? 'Nobody is waiting on a decision' : 'Nothing here'}
          body={filter === 'open' ? 'No customer has been paused twice within 6 months.' : 'No pauses match this filter.'}
        />
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' }, gap: 2 }}>
          <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', backgroundColor: '#fff', alignSelf: 'start' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1.4fr 0.8fr 1fr 0.7fr', gap: 2, px: 2, py: 1.25, backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              {['Customer', 'Counted', 'Paused since', 'Orders (all time)'].map((h) => (
                <Typography key={h} variant="caption" sx={{ fontWeight: 700, color: '#64748b', letterSpacing: 0.4 }}>{h.toUpperCase()}</Typography>
              ))}
            </Box>
            {reviews.map((r) => {
              const counted = r.events.filter((e) => !e.voided && e.counter === r.counter).length;
              return (
                <Box
                  key={r.pauseId}
                  component="button"
                  type="button"
                  onClick={() => { setSelected(r.pauseId); setInfo(null); }}
                  aria-pressed={r.pauseId === selected}
                  sx={{
                    display: 'grid', gridTemplateColumns: '1.4fr 0.8fr 1fr 0.7fr', gap: 2, px: 2, py: 1.5, width: '100%',
                    textAlign: 'left', border: 'none', borderBottom: '1px solid #eef2f6', cursor: 'pointer', fontFamily: 'inherit',
                    backgroundColor: r.pauseId === selected ? '#f1f5f9' : '#fff', '&:hover': { backgroundColor: '#fafbfc' },
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: '#1f2937' }}>{r.household.name ?? '—'}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{r.household.glpUserId}</Typography>
                  </Box>
                  <Typography variant="body2">{counted} {counterLabel(r.counter)}</Typography>
                  <Typography variant="body2">{at(r.startedAt)}</Typography>
                  <Typography variant="body2">{r.ordersAllTime}</Typography>
                </Box>
              );
            })}
          </Box>

          {current && (
            <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 2, backgroundColor: '#fff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 700, color: '#1f2937' }}>
                  {current.household.name ?? current.household.glpUserId} — {counterLabel(current.counter)}
                </Typography>
                <Pill
                  label={current.kind === 'review' ? (current.decision ? current.decision.replace('_', ' ').toUpperCase() : 'NEEDS A DECISION') : `LIFTS ${current.endsAt ? day(current.endsAt).toUpperCase() : ''}`}
                  fg={current.kind === 'review' && !current.decision ? '#b42318' : '#343F5B'}
                  bg={current.kind === 'review' && !current.decision ? '#fee4e2' : '#eef2f6'}
                />
              </Box>

              {current.events.map((e) => (
                <Box key={e.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: '1px solid #eef2f6' }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: e.voided ? '#94a3b8' : '#1f2937', textDecoration: e.voided ? 'line-through' : 'none' }}>
                      {day(e.occurredAt)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {eventDetail(e)}{e.voided ? ` · removed: ${e.voidReason ?? ''}` : ''}
                    </Typography>
                  </Box>
                  {canDecide && !e.voided && current.kind === 'review' && !current.decision && (
                    <Button
                      ariaLabel={`Remove the ${day(e.occurredAt)} event`}
                      buttonText="Remove (our error)"
                      variant="outlined"
                      size="small"
                      sx={DANGER_BTN}
                      disabled={busy}
                      onClick={() => void removeEvent(e)}
                    />
                  )}
                </Box>
              ))}

              {current.priorPauses.length > 0 && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1.5 }}>
                  Earlier pause{current.priorPauses.length > 1 ? 's' : ''}: {current.priorPauses.map((p) => at(p.startedAt)).join(', ')}
                </Typography>
              )}
              {current.decisionNote && (
                <Typography variant="caption" sx={{ display: 'block', color: '#1f2937', mt: 1.5 }}>
                  Note: {current.decisionNote}
                </Typography>
              )}

              {canDecide && current.kind === 'review' && !current.decision && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  <TextField
                    label="Note (required)"
                    value={note}
                    onChange={(ev) => setNote(ev.target.value)}
                    size="small"
                    multiline
                    fullWidth
                  />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      ariaLabel="Reinstate online ordering"
                      buttonText="Reinstate online ordering"
                      variant="contained"
                      color="primary"
                      disabled={busy}
                      onClick={() => void decide(current.pauseId, 'reinstated')}
                    />
                    <Button
                      ariaLabel="Keep paused"
                      buttonText="Keep paused"
                      variant="outlined"
                      sx={NEUTRAL_BTN}
                      disabled={busy}
                      onClick={() => void decide(current.pauseId, 'kept_paused')}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Remove an event only when we caused it (store closed, Runner off, our error). Removed events stop
                    counting; the pause is still yours to decide.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
